import React, {useState, useEffect} from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useUpdateDocumentMutation} from '../../../data/documentWorkspaceApi';
import PropTypes from 'prop-types';
import {DARK_TEXT_COLOR, WHITE_100} from '../../../theme';
import {decode} from 'html-entities';
import FolderIcon from '../../../assets/folder.svg';

const styles = {
  modal: {
    base: {
      borderRadius: '0.5rem',
      boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
      backgroundColor: WHITE_100,
      minHeight: '220px',
    },
    title: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      alignSelf: 'stretch',
      color: '#162523',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '150%',
      letterSpacing: '-0.14px',
    },
    divider: {
      borderBottomWidth: '0.0625rem',
      opacity: 1,
      color: '#DDe0DE',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: '500',
      lineHeight: '140%',
      color: DARK_TEXT_COLOR,
    },
  },
  createButton: {
    background: '#26A993',
    borderRadius: '0.5rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '2.25rem',
    color: WHITE_100,
    fontSize: '0.875rem',
    fontWeight: '600',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#229884',
    },
  },
  cancelButton: {
    borderRadius: '0.5rem',
    border: '1px solid #DDE0DE',
    background: WHITE_100,
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '2.25rem',
    color: DARK_TEXT_COLOR,
    fontSize: '0.875rem',
    fontWeight: '600',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  },
};

const MoveFileModal = ({
  selectedFolder,
  availableFolders,
  open,
  onClose,
  documentId,
  documentName,
  onSuccess,
  onError,
}) => {
  const [hoveredFolderId, setHoveredFolderId] = useState(null);
  const [updateDocument] = useUpdateDocumentMutation();

  useEffect(() => {
    if (!open) {
      setHoveredFolderId(null);
    }
  }, [open]);

  const handleClose = () => {
    setHoveredFolderId(null);
    onClose();
  };

  const handleSubmit = async (targetFolderId) => {
    if (documentId && targetFolderId) {
      try {
        const result = await updateDocument({
          documentId,
          data: {folderId: targetFolderId},
        });

        if (result.error) {
          throw new Error(result.error);
        }

        const targetFolder = availableFolders.find(
          (f) => f.id === targetFolderId
        );
        if (onSuccess) {
          onSuccess(`Document moved to ${targetFolder?.name || 'folder'}`);
        }
        handleClose();
      } catch (error) {
        console.error('Error moving document:', error);
        if (onError) {
          onError('Failed to move document. Please try again.');
        }
      }
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        position={'absolute'}
        sx={{left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
      >
        <Box width={'500px'} minWidth={'500px'} maxWidth={'500px'}>
          <Stack direction={'column'} sx={styles.modal.base}>
            <Stack
              direction={'row'}
              paddingY={'1rem'}
              paddingLeft={'1.5rem'}
              paddingRight={'1rem'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Typography sx={{...styles.modal.title, fontSize: '16px'}}>
                Move &ldquo;{decode(documentName || '')}&rdquo;
              </Typography>
              <IconButton size={'small'} onClick={handleClose}>
                <CloseIcon size={'1.25rem'} />
              </IconButton>
            </Stack>

            <Divider orientation={'horizontal'} sx={styles.modal.divider} />

            <Stack direction={'column'} padding={'1.5rem'} spacing={'1rem'}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={styles.modal.title}>
                  Current location:
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    border: '1px solid #DDE0DE',
                  }}
                >
                  <img
                    src={FolderIcon}
                    alt="Folder"
                    style={{width: 20, height: 20}}
                  />
                  <Typography
                    sx={{fontSize: '14px', fontWeight: 500, color: '#162523'}}
                  >
                    {selectedFolder?.name}
                  </Typography>
                </Box>
              </Stack>
              <Typography sx={styles.modal.title}>Move to:</Typography>
              <Box
                sx={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #DDE0DE',
                  borderRadius: '8px',
                  padding: '0.5rem',
                }}
              >
                <Stack direction="column" spacing={2}>
                  {availableFolders.length === 0 ? (
                    <Typography
                      sx={{
                        color: '#7E8986',
                        textAlign: 'center',
                        padding: '2rem',
                      }}
                    >
                      No folders available to move to
                    </Typography>
                  ) : (
                    availableFolders.map((folder) => {
                      const folderId = folder.id;
                      const isHovered = hoveredFolderId === folderId;

                      return (
                        <Box
                          key={folderId}
                          onMouseEnter={() => setHoveredFolderId(folderId)}
                          onMouseLeave={() => setHoveredFolderId(null)}
                          sx={{
                            height: '4rem',
                            border: '1px solid #DFE5E2',
                            borderRadius: '8px',
                            padding: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            '&:hover': {
                              borderColor: '#000000',
                            },
                          }}
                          onClick={() => handleSubmit(folderId)}
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            flex={1}
                          >
                            <img
                              src={FolderIcon}
                              alt="Folder"
                              style={{width: 24, height: 24}}
                            />
                            <Stack direction="column" spacing={0.5}>
                              <Typography variant="h3" sx={{fontWeight: 500}}>
                                {folder.name}
                              </Typography>
                              <Typography variant="caption">
                                {folder.documentCount} Documents
                              </Typography>
                            </Stack>
                          </Stack>
                          {isHovered && (
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => handleSubmit(folderId)}
                              sx={{
                                color: '#26A993',
                                fontSize: '0.875rem',
                                fontWeight: '400',
                                textTransform: 'none',
                                padding: '4px 8px',
                                minWidth: 'auto',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  color: '#229884',
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              Move
                            </Button>
                          )}
                        </Box>
                      );
                    })
                  )}
                </Stack>
              </Box>
              <Divider orientation={'horizontal'} sx={styles.modal.divider} />
              <Stack direction="column" spacing={2}>
                <Typography
                  sx={{
                    color: '#043B34',
                    textAlign: 'center',
                    padding: '0.5rem',
                  }}
                >
                  Please reach out to support if you&apos;d like to move a
                  report to a different year.
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

export default MoveFileModal;

MoveFileModal.propTypes = {
  selectedFolder: PropTypes.object.isRequired,
  availableFolders: PropTypes.array.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  documentId: PropTypes.string.isRequired,
  documentName: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};
